import { useState, useEffect } from 'react';
import { placeOrder, initializePayment, verifyPayment, fetchSettings } from '../db';
import { toast } from '../toast';

// Nigerian states and sample cities
const NG_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT - Abuja', 'Gombe',
  'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
  'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
  'Taraba', 'Yobe', 'Zamfara',
];

export default function CheckoutPage({ cartItems: initialCartItems, onNavigate, user }) {
  const storageKey = `mb_addresses_${user?.id || 'guest'}`;
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
  })();

  const nameParts = (storedUser.name || '').trim().split(' ');
  const preFirstName = nameParts[0] || '';
  const preLastName = nameParts.slice(1).join(' ') || '';

  // Local editable copy of cart for the summary panel
  const [localCart] = useState(() => {
    // Aggregate duplicates by id and size
    const map = {};
    (initialCartItems || []).forEach(item => {
      const key = `${item.id}${item.size ? '-' + item.size : ''}`;
      if (!map[key]) map[key] = { ...item, quantity: 0 };
      map[key].quantity += (item.quantity || 1);
    });
    return Object.values(map);
  });

  const [form, setForm] = useState({
    firstName: preFirstName,
    lastName: preLastName,
    email: storedUser.email || '',
    phone: storedUser.phone || '+234',
    country: 'Nigeria',
    state: '',
    city: '',
    streetAddress: '',
    pickupNote: '',
  });
  const [isStorePickup, setIsStorePickup] = useState(false);
  const [pickupLocation, setPickupLocation] = useState('Lagos');

  const [isProcessing, setIsProcessing] = useState(false);
  const [paystackKey, setPaystackKey] = useState('');

  // Saved addresses
  const [savedAddresses] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; } catch { return []; }
  });
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  const [showAddressPicker, setShowAddressPicker] = useState(false);

  const [saveAddress, setSaveAddress] = useState(false);
  const [shippingConfig, setShippingConfig] = useState(null);

  useEffect(() => {
    fetchSettings().then(settings => {
      if (settings?.paystack_public_key) setPaystackKey(settings.paystack_public_key);
      setShippingConfig({
        enabled: settings.shipping_enabled !== 'false',
        lagos_kwara: Number(settings.shipping_fee_lagos_kwara) || 2000,
        other: Number(settings.shipping_fee_other) || 4000
      });
    }).catch(() => { });
  }, []);

  // When user picks a saved address, fill the shipping fields
  const applySavedAddress = (addr) => {
    const nameParts = (addr.fullName || '').trim().split(' ');
    const first = nameParts[0] || '';
    const last = nameParts.slice(1).join(' ') || '';

    setForm(prev => ({
      ...prev,
      firstName: first || prev.firstName,
      lastName: last || prev.lastName,
      phone: addr.phone || prev.phone,
      state: addr.state || '',
      city: addr.city || '',
      streetAddress: addr.addressLine || '',
    }));
    setSelectedSavedAddress(addr);
    setShowAddressPicker(false);
    toast.success(`Address "${addr.fullName}" applied`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'state' ? { city: '' } : {}),
    }));
    // Clear saved address selection if user manually edits
    if (['state', 'city', 'streetAddress'].includes(name)) setSelectedSavedAddress(null);
  };

  const subtotal = localCart.reduce((sum, item) => {
    const price = typeof item.price === 'number'
      ? item.price
      : (parseFloat(String(item.price).replace(/[^\d.]/g, '')) || 0);
    return sum + (price * (item.quantity || 1));
  }, 0);

  const formStateLower = (form.state || '').toLowerCase();
  
  let shippingFee = 0;
  
  const allFreeShipping = localCart.every(item => item.is_free_shipping);
  
  if (!isStorePickup && shippingConfig && shippingConfig.enabled && !allFreeShipping) {
    if (form.state) {
      shippingFee = (formStateLower === 'lagos' || formStateLower === 'kwara') 
        ? shippingConfig.lagos_kwara 
        : shippingConfig.other;
    }
  }

  const total = subtotal + shippingFee;

  const isFormComplete = isStorePickup
    ? !!(form.firstName && form.lastName && form.email && form.phone)
    : !!(form.firstName && form.lastName && form.email && form.phone && form.state && form.city && form.streetAddress);

  const handlePlaceOrderClick = async () => {
    const { firstName, lastName, email, phone, country, state, city, streetAddress } = form;
    if (!firstName || !lastName || !email || !phone) {
      toast.error('Please fill out all required fields.');
      return;
    }
    if (!isStorePickup && (!state || !city || !streetAddress)) {
      toast.error('Please fill out all required shipping fields.');
      return;
    }
    if (!localCart.length) {
      toast.error('Your cart is empty.');
      return;
    }
    if (!paystackKey) {
      toast.error('Payment gateway not configured. Please contact support.');
      return;
    }

    setIsProcessing(true);
    try {
      const fullAddress = isStorePickup
        ? `Store Pickup (${pickupLocation})`
        : `${streetAddress}, ${city}, ${state}, ${country}`;
      // One entry per product with quantity
      const deduped = localCart.map(item => ({ product_id: item.id, quantity: item.quantity, size: item.size, color: item.color }));

      const order = await placeOrder(deduped, fullAddress, fullAddress, phone, isStorePickup ? pickupLocation : state);
      const initData = await initializePayment(order.id);
      const amountKobo = initData.amountKobo || Math.round(total * 100);

      // Save address if requested
      if (saveAddress) {
        const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const newAddr = {
          id: Date.now().toString(),
          fullName: `${firstName} ${lastName}`.trim(),
          phone,
          state,
          city,
          addressLine: streetAddress,
          isDefault: existing.length === 0
        };
        // avoid exact duplicates
        const isDup = existing.find(a => 
          a.fullName === newAddr.fullName && 
          a.addressLine === newAddr.addressLine
        );
        if (!isDup) {
          localStorage.setItem(storageKey, JSON.stringify([...existing, newAddr]));
        }
      }

      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: email,
        amount: amountKobo,
        access_code: initData.accessCode,
        onClose: () => {
          toast.info('Payment window closed. Your order is saved — you can pay later from Account.');
          setIsProcessing(false);
          // Navigate to account page where they can see their unpaid orders
          onNavigate('account');
        },
        callback: (response) => {
          verifyPayment(response.reference || initData.transactionReference)
            .then(() => { setIsProcessing(false); onNavigate('orderconfirmation', { orderId: order.reference, newOrder: order }); })
            .catch((err) => {
              const msg = err.response?.data?.message || err.message;
              toast.error(`Verification failed: ${msg}. Contact support if debited.`);
              setIsProcessing(false);
            });
        },
      });
      handler.openIframe();
    } catch (error) {
      console.error('Order error:', error);
      const msg = error.response?.data?.message || 'Something went wrong processing your order. Please try again.';
      toast.error(msg);
      setIsProcessing(false);
    }
  };

  const inputCls = "w-full p-3.5 border border-gray-200 rounded-xl text-sm focus:border-[#F47B20] outline-none transition-colors bg-white disabled:bg-gray-50";
  const selectCls = "w-full p-3.5 border border-gray-200 rounded-xl text-sm focus:border-[#F47B20] outline-none transition-colors bg-white appearance-none cursor-pointer disabled:bg-gray-50";
  const labelCls = "block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5";

  return (
    <div className="min-h-screen bg-[#FAF3F0] pb-24 lg:pb-8">
      {/* Header */}
      <div className="px-4 lg:px-8 py-4 border-b border-gray-100 bg-white sticky top-0 z-10 flex items-center gap-3 shadow-sm">
        <button
          onClick={() => onNavigate('cart')}
          className="w-10 h-10 bg-gray-50 hover:bg-gray-100 rounded-full flex items-center justify-center border border-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-[#1A1A2E]">Checkout</h1>
      </div>

      <div className="w-full mx-auto px-4 lg:px-12 xl:px-16 pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

          {/* ── Shipping Form ── */}
          <div className="space-y-5">

            {/* Personal Info */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-6 h-6 shrink-0 bg-[#FEF0E6] rounded-full flex items-center justify-center">
                  <span className="text-[#F47B20] text-xs font-black">1</span>
                </div>
                <h2 className="text-base font-bold text-[#1A1A2E] whitespace-nowrap">Personal Information</h2>
                {storedUser.name && (
                  <span className="ml-auto text-[9px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full font-semibold shrink-0 whitespace-nowrap">Auto-filled</span>
                )}
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>First Name *</label>
                    <input type="text" name="firstName" value={form.firstName} onChange={handleChange}
                      className={inputCls} placeholder="First name" disabled={isProcessing} />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name *</label>
                    <input type="text" name="lastName" value={form.lastName} onChange={handleChange}
                      className={inputCls} placeholder="Last name" disabled={isProcessing} />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Email Address *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange}
                    className={inputCls} placeholder="Your email address" disabled={isProcessing} />
                </div>

                <div>
                  <label className={labelCls}>Phone Number *</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                    onFocus={(e) => { if (!e.target.value) handleChange({ target: { name: 'phone', value: '+234 ' } }); }}
                    onBlur={(e) => { if (e.target.value.trim() === '+234') handleChange({ target: { name: 'phone', value: '' } }); }}
                    className={inputCls} placeholder="+234 80..." disabled={isProcessing} />
                </div>
              </div>
            </div>

            {/* Shipping / Pickup */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 shrink-0 bg-[#FEF0E6] rounded-full flex items-center justify-center">
                  <span className="text-[#F47B20] text-xs font-black">2</span>
                </div>
                <h2 className="text-base font-bold text-[#1A1A2E] whitespace-nowrap">Delivery / Pickup</h2>
              </div>

              {/* Delivery method toggle */}
              <div className="flex rounded-2xl border border-gray-200 overflow-hidden mb-5">
                <button
                  type="button"
                  onClick={() => setIsStorePickup(false)}
                  className={`flex-1 py-2.5 text-sm font-bold transition-colors ${
                    !isStorePickup ? 'bg-[#F47B20] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  🚚 Home Delivery
                </button>
                <button
                  type="button"
                  onClick={() => setIsStorePickup(true)}
                  className={`flex-1 py-2.5 text-sm font-bold transition-colors ${
                    isStorePickup ? 'bg-[#F47B20] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  🏪 Store Pickup
                </button>
              </div>

              {isStorePickup ? (
                <div className="space-y-4">
                  <div className="rounded-2xl bg-[#FEF0E6]/60 border border-[#F47B20]/20 p-4 text-sm text-[#1A1A2E]">
                    <p className="font-bold mb-3">📍 Select Store Location</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${pickupLocation === 'Lagos' ? 'border-[#F47B20] bg-white' : 'border-gray-200 bg-white/50 hover:bg-white'}`}>
                        <input type="radio" name="pickupLocation" value="Lagos" checked={pickupLocation === 'Lagos'} onChange={(e) => setPickupLocation(e.target.value)} className="text-[#F47B20] focus:ring-[#F47B20]" />
                        <span className="font-semibold text-sm">Lagos Store</span>
                      </label>
                      <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${pickupLocation === 'Ilorin' ? 'border-[#F47B20] bg-white' : 'border-gray-200 bg-white/50 hover:bg-white'}`}>
                        <input type="radio" name="pickupLocation" value="Ilorin" checked={pickupLocation === 'Ilorin'} onChange={(e) => setPickupLocation(e.target.value)} className="text-[#F47B20] focus:ring-[#F47B20]" />
                        <span className="font-semibold text-sm">Ilorin Store</span>
                      </label>
                    </div>
                    <p className="text-gray-600 text-xs mt-3 leading-relaxed">No shipping fee applies. We'll contact you when your order is ready for pickup at our {pickupLocation} location.</p>
                  </div>
                </div>
              ) : (
                <>
              {/* Saved address picker */}
              {savedAddresses.length > 0 && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => setShowAddressPicker(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-dashed border-[#F47B20]/60 bg-[#FEF0E6]/40 hover:bg-[#FEF0E6] transition-colors text-sm font-semibold text-[#F47B20]"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {selectedSavedAddress ? `Using: ${selectedSavedAddress.fullName}` : 'Use a saved address'}
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${showAddressPicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showAddressPicker && (
                    <div className="mt-2 space-y-2 border border-gray-100 rounded-2xl p-3 bg-gray-50 max-h-52 overflow-y-auto">
                      {savedAddresses.map(addr => (
                        <button
                          key={addr.id}
                          type="button"
                          onClick={() => applySavedAddress(addr)}
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-all text-sm ${selectedSavedAddress?.id === addr.id
                            ? 'border-[#F47B20] bg-[#FEF0E6] text-[#F47B20]'
                            : 'border-gray-200 bg-white hover:border-[#F47B20]/40'
                            }`}
                        >
                          <p className="font-bold text-[#1A1A2E] text-xs mb-0.5">
                            {addr.fullName}
                            {addr.isDefault && <span className="ml-2 text-[10px] bg-[#FEF0E6] text-[#F47B20] px-1.5 py-0.5 rounded-full">Default</span>}
                          </p>
                          <p className="text-gray-500 text-xs truncate">{addr.addressLine}, {addr.city}, {addr.state}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {/* Country */}
                <div>
                  <label className={labelCls}>Country *</label>
                  <div className="relative">
                    <select name="country" value={form.country} onChange={handleChange}
                      className={selectCls} disabled={isProcessing}>
                      <option value="Nigeria">🇳🇬 Nigeria</option>
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* State */}
                <div>
                  <label className={labelCls}>State *</label>
                  <div className="relative">
                    <select name="state" value={form.state} onChange={handleChange}
                      className={selectCls} disabled={isProcessing}>
                      <option value="">Select State…</option>
                      {NG_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* City */}
                <div>
                  <label className={labelCls}>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className={inputCls}
                    placeholder="City"
                    disabled={isProcessing}
                  />
                </div>

                {/* Street Address */}
                <div>
                  <label className={labelCls}>Delivery Address / Pickup Point *</label>
                  <textarea
                    name="streetAddress"
                    value={form.streetAddress}
                    onChange={handleChange}
                    rows={3}
                    className="w-full p-3.5 border border-gray-200 rounded-xl text-sm focus:border-[#F47B20] outline-none transition-colors resize-none bg-white disabled:bg-gray-50"
                    placeholder="Street address or Pickup point"
                    disabled={isProcessing}
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Enter your home address or write "Pickup" for in-store collection.</p>
                </div>

                {/* Save Address Checkbox */}
                <div className="pt-2">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${saveAddress ? 'bg-[#F47B20] border-[#F47B20]' : 'border-gray-300 bg-white group-hover:border-[#F47B20]'}`}>
                      {saveAddress && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-[#1A1A2E]">Save this information for next time</span>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={saveAddress}
                      onChange={(e) => setSaveAddress(e.target.checked)}
                      disabled={isProcessing}
                    />
                  </label>
                </div>
              </div>
              </>
              )}
            </div>
          </div>

          {/* ── Order Summary ── */}
          <div>
            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 sticky top-24">
              <h2 className="text-base font-bold text-[#1A1A2E] mb-4 border-b border-gray-100 pb-3">Order Summary</h2>

              {localCart.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-6">No items in cart.</p>
              ) : (
                <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto pr-1">
                  {localCart.map((item, idx) => {
                    const price = typeof item.price === 'number'
                      ? item.price
                      : (parseFloat(String(item.price).replace(/[^\d.]/g, '')) || 0);
                    return (
                      <div key={`${item.id}-${item.size || idx}`} className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#FAF3F0] rounded-xl shrink-0 border border-gray-100 overflow-hidden flex items-center justify-center">
                          {(() => {
                            const itemImg = Array.isArray(item.images)
                              ? item.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i))
                              : (!item.image?.match(/\.(mp4|webm|ogg)$/i) ? item.image : null);
                            if (itemImg) return <img src={itemImg} alt={item.name} className="w-full h-full object-cover" />;
                            return <span className="text-xl">{item.emoji || '🛍️'}</span>;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-[#1A1A2E] leading-tight line-clamp-1">{item.name}</p>
                          {item.size && <p className="text-[10px] text-gray-400 mt-0.5">Size: {item.size}</p>}
                          <p className="text-[10px] text-gray-400">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-bold text-xs text-[#F47B20] shrink-0">₦{(price * item.quantity).toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-gray-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Subtotal</span><span className="font-bold text-[#1A1A2E]">₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Shipping</span>
                  <span className="font-bold text-[#F47B20]">
                    {isStorePickup
                      ? 'Store Pickup (Free)'
                      : shippingFee > 0
                        ? `₦${shippingFee.toLocaleString()}`
                        : (form.state ? 'Free Shipping' : '—')}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 leading-tight">(Fees estimated; may adjust based on location. You'll be notified of any changes.)</p>
                <div className="flex justify-between pt-3 border-t border-gray-100">
                  <span className="font-black text-base text-[#1A1A2E]">Total</span>
                  <span className="font-black text-lg text-[#F47B20]">₦{total.toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={handlePlaceOrderClick}
                disabled={!localCart.length || isProcessing || !isFormComplete}
                className="w-full mt-5 bg-[#1A1A2E] hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl text-base transition-colors shadow-sm flex justify-center items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing Payment...
                  </>
                ) : 'Place Order & Pay'}
              </button>

              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secured by Paystack
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
